var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PropsDiff } from '@seddi/core';
import { LogoTechnique, PieceSide } from '@seddi/scene';
import { SurfaceMaterialModel, TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import { produce } from 'immer';
import { isInside as isLogoInside } from '@/domain/logos/logoBounds';
import { logoTextureImageSrc, normalizeRecoloredImageSrc } from '@/domain/mockups/mockupLogos';
import { calculateHoverTextureSize, createHoverTexture, createLogoRegionTexture, regionSizeToLineSizes, regionTextureSizeNumbers, } from './viewerLogoRegionTexture';
/**
 * Create new scene context.
 */
export const create = () => {
    return {
        hover: {
            isDirty: false,
            useSelectedColor: false,
            scene: {},
            state: undefined,
        },
        logos: { isDirty: false, scene: {}, state: [] },
        selection: { isDirty: false },
        styleRegions: { isDirty: false, scene: {}, state: [] },
        styleScene: { isDirty: false },
    };
};
/**
 * Set style regions (with entityId for each region).
 */
export const setStyleRegions = (self, state) => {
    self.styleRegions.isDirty = true;
    self.styleRegions.state = state;
    self.logos.isDirty = true;
    self.logos.state.forEach(l => updateLogoRegion(self, l.id, l.region));
};
/**
 * Set style scene (base scene without logos).
 */
export const setStyleScene = (self, state, hideTearAwayLabel, useBrandLogos) => {
    self.styleScene.isDirty = true;
    if (hideTearAwayLabel || !useBrandLogos) {
        const transformedState = produce(state, scene => {
            scene.children.forEach(child => {
                var _a, _b;
                if ((_b = (_a = child.components) === null || _a === void 0 ? void 0 : _a.garmentPiece) === null || _b === void 0 ? void 0 : _b.logos) {
                    child.components.garmentPiece.logos = child.components.garmentPiece.logos.filter((logo) => {
                        if (hideTearAwayLabel && logo.isTearAwayLabel) {
                            return false;
                        }
                        if (!useBrandLogos && logo.isBrandLogo) {
                            return false;
                        }
                        return true;
                    });
                }
            });
        });
        self.styleScene.state = transformedState;
    }
    else {
        self.styleScene.state = state;
    }
};
/**
 * Reconcile selection (draw region border around selected logo).
 */
export const reconcileSelection = (self, next) => __awaiter(void 0, void 0, void 0, function* () {
    const prev = self.selection.state;
    if (prev === next)
        return;
    if (!next) {
        clearSelection(self);
    }
    else if (!prev || prev.id !== next.id) {
        yield createSelection(self, next);
    }
    else {
        if (prev.region !== next.region) {
            yield createSelection(self, next);
        }
        else {
            updateSelectionResource(self, next);
        }
    }
    self.selection.isDirty = true;
    self.selection.state = next;
});
/**
 * Reconcile hover (draw logo border when hovering or when selected).
 * @param next Logo to show border around (hover or selected).
 * @param useSelectedColor Use selected color (darker) instead of hover color.
 */
export const reconcileHover = (self_1, next_1, ...args_1) => __awaiter(void 0, [self_1, next_1, ...args_1], void 0, function* (self, next, useSelectedColor = false) {
    const prev = self.hover.state;
    const prevUseSelectedColor = self.hover.useSelectedColor;
    if (isMockupLogoEqual(prev, next) && prevUseSelectedColor === useSelectedColor) {
        return;
    }
    if (!next) {
        clearHover(self);
    }
    else if ((!prev && next) || prev.id !== next.id || prevUseSelectedColor !== useSelectedColor) {
        yield createHover(self, next, useSelectedColor);
    }
    else if (prev.region !== next.region) {
        yield createHover(self, next, useSelectedColor);
    }
    else {
        if (!isVector2Equal(prev.size, next.size)) {
            yield updateHoverSize(self, next.size);
        }
        if (!isTransformEqual(prev.transform, next.transform)) {
            updateHoverTransform(self, next.transform);
        }
    }
    self.hover.isDirty = true;
    self.hover.useSelectedColor = useSelectedColor;
    self.hover.state = next;
});
/**
 * Compute diff between previous and next logo arrays.
 * Returns tuples of [prev, next] for each changed logo (added, removed, or updated).
 */
const getDiffLogos = (prev, next) => {
    const prevProps = prev.reduce((map, l) => {
        map[l.id] = l;
        return map;
    }, {});
    const nextProps = next.reduce((map, l) => {
        map[l.id] = l;
        return map;
    }, {});
    const idsFromProps = PropsDiff.diff(nextProps, prevProps);
    // TODO: This part is not in Decorator
    const idsFromImage = next
        .filter(l => prevProps[l.id] &&
        (prevProps[l.id].imageSrc !== l.imageSrc ||
            logoTextureImageSrc(prevProps[l.id]) !== logoTextureImageSrc(l)))
        .map(l => l.id);
    const ids = [...new Set([...idsFromProps, ...idsFromImage])];
    return ids.map(id => [prevProps[id], nextProps[id]]);
};
const isMockupLogosEqual = (a, b) => {
    if (a === b)
        return true;
    if (a.length !== b.length)
        return false;
    return a.every((logo, index) => isMockupLogoEqual(logo, b[index]));
};
/**
 * Reconcile logos using diff-based updates.
 */
export const reconcileLogos = (self, next) => {
    const prev = self.logos.state;
    if (isMockupLogosEqual(prev, next)) {
        return;
    }
    const diff = getDiffLogos(prev, next);
    for (const tuple of diff) {
        reconcileLogo(self, tuple[0], tuple[1]);
    }
    self.logos.isDirty = true;
    self.logos.state = next;
};
/**
 * Update scene descriptor by composing base scene with logos and selection.
 */
export const updateScene = (self) => {
    const { hover, logos, selection, styleScene } = self;
    if (!hover.isDirty && !logos.isDirty && !selection.isDirty && !styleScene.isDirty) {
        return;
    }
    styleScene.isDirty = false;
    if (!styleScene.state) {
        self.scene = undefined;
        return;
    }
    self.scene = styleScene.state;
    const { styleRegions } = self;
    const logosState = logos.state;
    for (const logoState of logosState) {
        const regionState = styleRegions.state.find(r => r.id === logoState.region);
        if (!regionState) {
            continue;
        }
        const logoScene = logos.scene[logoState.id];
        const entityId = regionState.entityId;
        if (!logoScene || !entityId) {
            continue;
        }
        self.scene = produce(self.scene, scene => {
            var _a;
            var _b;
            const entity = scene.children.find((e) => e._id === entityId);
            if (!entity) {
                return;
            }
            pushLogo(entity, logoScene.piece);
            (_a = (_b = scene).logos) !== null && _a !== void 0 ? _a : (_b.logos = []);
            scene.logos.push(logoScene.resource);
        });
    }
    logos.isDirty = false;
    // Hover reconciliation: draw logo border when hovering.
    if (hover.state && hover.scene.piece && hover.scene.resource) {
        const regionState = styleRegions.state.find(r => r.id === hover.state.region);
        if (regionState === null || regionState === void 0 ? void 0 : regionState.entityId) {
            self.scene = produce(self.scene, scene => {
                var _a;
                var _b;
                const entity = scene.children.find((e) => e._id === regionState.entityId);
                if (!entity)
                    return;
                pushLogo(entity, hover.scene.piece);
                (_a = (_b = scene).logos) !== null && _a !== void 0 ? _a : (_b.logos = []);
                scene.logos.push(hover.scene.resource);
            });
        }
    }
    hover.isDirty = false;
    // Selection reconciliation: draw style region border around selected logo.
    if (selection.state) {
        const regionState = styleRegions.state.find(r => r.id === selection.state.region);
        const regionScene = styleRegions.scene[selection.state.region];
        if (regionState && regionScene && selection.scene) {
            const entityId = regionState.entityId;
            if (entityId) {
                self.scene = produce(self.scene, scene => {
                    var _a;
                    var _b;
                    const entity = scene.children.find((e) => e._id === entityId);
                    if (!entity) {
                        return;
                    }
                    pushLogo(entity, selection.scene);
                    (_a = (_b = scene).logos) !== null && _a !== void 0 ? _a : (_b.logos = []);
                    scene.logos.push(regionScene.resource, regionScene.resourceWarn);
                });
            }
        }
    }
    selection.isDirty = false;
};
const createLogoResource = (id, imageSrc, size) => {
    return {
        id,
        materials: [
            {
                id,
                model: SurfaceMaterialModel.DISNEY,
                params: {
                    baseColorMap: {
                        id,
                        href: imageSrc,
                        colorSpace: TextureColorSpace.GAMMA,
                        format: TextureFormat.PNG,
                    },
                    roughness: 0.3,
                    specular: 0.8,
                },
            },
        ],
        name: id,
        size,
        _id: id,
        image: imageSrc,
    };
};
const clearSelection = (self) => {
    self.selection.scene = undefined;
    self.selection.state = undefined;
};
const clearHover = (self) => {
    self.hover.scene.piece = undefined;
    self.hover.scene.resource = undefined;
    self.hover.state = undefined;
};
const createLogoBorderTexture = (logoSize, useSelectedColor) => __awaiter(void 0, void 0, void 0, function* () {
    const id = crypto.randomUUID();
    const imageBitmap = yield createHoverTexture(logoSize, useSelectedColor);
    const size = calculateHoverTextureSize(logoSize);
    return {
        id,
        materials: [
            {
                id,
                model: SurfaceMaterialModel.DISNEY,
                params: {
                    baseColorMap: {
                        id,
                        imageBitmap,
                        colorSpace: TextureColorSpace.GAMMA,
                    },
                    roughness: 0.2,
                    specular: 0.2,
                },
            },
        ],
        name: id,
        size,
        _id: id,
    };
});
const createHover = (self, logo, useSelectedColor) => __awaiter(void 0, void 0, void 0, function* () {
    const regionState = self.styleRegions.state.find(r => r.id === logo.region);
    if (!regionState)
        return;
    const resource = yield createLogoBorderTexture(logo.size, useSelectedColor);
    self.hover.scene.resource = resource;
    self.hover.scene.piece = {
        _id: `${logo.id}:hover`,
        meshId: regionState.pieceName,
        resource: resource.id,
        size: calculateHoverTextureSize(logo.size),
        side: regionState.side || PieceSide.FRONT,
        technique: LogoTechnique.Rubber,
        transform: logo.transform,
        pivot: { x: 0, y: 0 },
        name: regionState.name,
    };
});
const updateHoverSize = (self, size) => __awaiter(void 0, void 0, void 0, function* () {
    const prev = self.hover.state;
    if (!prev || !self.hover.scene.piece)
        return;
    const useSelectedColor = self.hover.useSelectedColor;
    const resource = yield createLogoBorderTexture(size, useSelectedColor);
    self.hover.scene.resource = resource;
    self.hover.scene.piece = Object.assign(Object.assign({}, self.hover.scene.piece), { size: calculateHoverTextureSize(size), resource: resource.id });
});
const updateHoverTransform = (self, transform) => {
    if (!self.hover.scene.piece)
        return;
    self.hover.scene.piece = Object.assign(Object.assign({}, self.hover.scene.piece), { transform });
};
const createRegion = (self, region) => __awaiter(void 0, void 0, void 0, function* () {
    const { id, size } = region;
    const [imageBitmap, imageBitmap2] = yield Promise.all([
        createLogoRegionTexture(size, 'black', 'white'),
        createLogoRegionTexture(size, 'black', 'red'),
    ]);
    const resourceId = crypto.randomUUID();
    const resourceId2 = crypto.randomUUID();
    self.styleRegions.scene[id] = {
        resource: createRegionResource(resourceId, imageBitmap, size),
        resourceWarn: createRegionResource(resourceId2, imageBitmap2, size),
    };
});
const createRegionResource = (id, imageBitmap, size) => {
    const lineSizes = regionSizeToLineSizes(size);
    const regionSize = regionTextureSizeNumbers(size, lineSizes.lineWidth).metersSize;
    return {
        id,
        image: 'unused',
        materials: [
            {
                id,
                model: SurfaceMaterialModel.DISNEY,
                params: {
                    baseColorMap: {
                        id,
                        imageBitmap,
                        colorSpace: TextureColorSpace.GAMMA,
                    },
                    roughness: 1.0,
                    specular: 0.5,
                },
            },
        ],
        name: id,
        size: Object.assign({}, regionSize),
        _id: id,
    };
};
const createSelection = (self, logo) => __awaiter(void 0, void 0, void 0, function* () {
    const region = logo.region;
    const regionState = self.styleRegions.state.find(r => r.id === region);
    if (!regionState)
        return;
    let regionScene = self.styleRegions.scene[region];
    if (!regionScene) {
        yield createRegion(self, regionState);
        regionScene = self.styleRegions.scene[region];
    }
    if (!regionScene)
        return;
    const isInside = isLogoInside(logo.size, logo.transform, regionState.size, regionState.transform);
    const resource = isInside ? regionScene.resource.id : regionScene.resourceWarn.id;
    const lineSizes = regionSizeToLineSizes(regionState.size);
    const size = regionTextureSizeNumbers(regionState.size, lineSizes.lineWidth).metersSize;
    self.selection.scene = {
        _id: crypto.randomUUID(),
        meshId: regionState.pieceName,
        resource,
        size,
        side: regionState.side || PieceSide.FRONT,
        technique: LogoTechnique.Rubber,
        transform: regionState.transform,
        pivot: { x: 0, y: 0 },
        name: regionState.name,
    };
});
const updateSelectionResource = (self, next) => {
    const regionState = self.styleRegions.state.find(r => r.id === next.region);
    const regionScene = self.styleRegions.scene[next.region];
    if (!regionState || !regionScene || !self.selection.scene)
        return;
    const isInside = isLogoInside(next.size, next.transform, regionState.size, regionState.transform);
    const resourceId = isInside ? regionScene.resource.id : regionScene.resourceWarn.id;
    if (self.selection.scene.resource !== resourceId) {
        self.selection.scene = Object.assign(Object.assign({}, self.selection.scene), { resource: resourceId });
    }
};
const pushLogo = (entity, logo) => {
    var _a, _b, _c, _d;
    if ((_a = entity.components) === null || _a === void 0 ? void 0 : _a.garmentPiece) {
        entity.components.garmentPiece.logos = (_b = entity.components.garmentPiece.logos) !== null && _b !== void 0 ? _b : [];
        entity.components.garmentPiece.logos.push(logo);
    }
    if ((_c = entity.components) === null || _c === void 0 ? void 0 : _c.model) {
        entity.components.model.logos = (_d = entity.components.model.logos) !== null && _d !== void 0 ? _d : [];
        entity.components.model.logos.push(logo);
    }
};
const clearLogo = (self, id) => {
    const logo = self.logos.state.find(l => l.id === id);
    if (logo === null || logo === void 0 ? void 0 : logo.recoloredId) {
        delete self.logos.scene[id];
        delete self.logos.scene[logo.recoloredId];
    }
    else {
        delete self.logos.scene[id];
    }
};
const createLogo = (self, logo) => {
    var _a;
    const id = logo.id;
    const imageSrcToUse = logoTextureImageSrc(logo);
    if (!imageSrcToUse) {
        return;
    }
    const regionState = self.styleRegions.state.find(r => r.id === logo.region);
    if (!regionState) {
        return;
    }
    const recolorUrl = normalizeRecoloredImageSrc(logo.recoloredImageSrc);
    const resourceId = recolorUrl && logo.recoloredId ? logo.recoloredId : crypto.randomUUID();
    const resource = createLogoResource(resourceId, imageSrcToUse, logo.size);
    const piece = {
        _id: id,
        meshId: regionState.pieceName,
        resource: resourceId,
        size: logo.size,
        side: regionState.side || PieceSide.FRONT,
        technique: logo.technique,
        transform: logo.transform,
        pivot: { x: 0, y: 0 },
        name: (_a = logo.displayName) !== null && _a !== void 0 ? _a : logo.name,
    };
    self.logos.scene[id] = { piece, resource };
};
const reconcileLogo = (self, prev, next) => {
    if (!next) {
        if (prev) {
            clearLogo(self, prev.id);
        }
    }
    else if (!prev) {
        createLogo(self, next);
    }
    else {
        const imageChanged = prev.recoloredId !== next.recoloredId ||
            prev.imageSrc !== next.imageSrc ||
            logoTextureImageSrc(prev) !== logoTextureImageSrc(next);
        if (imageChanged) {
            clearLogo(self, prev.id);
            createLogo(self, next);
        }
        else {
            if (prev.region !== next.region) {
                updateLogoRegion(self, next.id, next.region);
            }
            if (!isVector2Equal(prev.size, next.size)) {
                updateLogoSize(self, next.id, next.size);
            }
            if (prev.technique !== next.technique) {
                updateLogoTechnique(self, next.id, next.technique);
            }
            if (!isTransformEqual(prev.transform, next.transform)) {
                updateLogoTransform(self, next.id, next.transform);
            }
        }
    }
};
const updateLogoRegion = (self, id, region) => {
    const scene = self.logos.scene[id];
    const regionState = self.styleRegions.state.find(r => r.id === region);
    if (!scene || !regionState)
        return;
    scene.piece = Object.assign(Object.assign({}, scene.piece), { meshId: regionState.pieceName, side: regionState.side || PieceSide.FRONT });
};
const updateLogoSize = (self, id, size) => {
    const scene = self.logos.scene[id];
    if (!scene)
        return;
    scene.piece = Object.assign(Object.assign({}, scene.piece), { size });
};
const updateLogoTechnique = (self, id, technique) => {
    const scene = self.logos.scene[id];
    if (!scene)
        return;
    scene.piece = Object.assign(Object.assign({}, scene.piece), { technique });
};
const updateLogoTransform = (self, id, transform) => {
    const scene = self.logos.scene[id];
    if (!scene)
        return;
    scene.piece = Object.assign(Object.assign({}, scene.piece), { transform });
};
const isVector2Equal = (a, b) => a.x === b.x && a.y === b.y;
const isTransformEqual = (a, b) => a.translation.x === b.translation.x &&
    a.translation.y === b.translation.y &&
    a.rotation === b.rotation;
const isMockupLogoEqual = (a, b) => {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    return (a.id === b.id &&
        a.name === b.name &&
        a.region === b.region &&
        a.technique === b.technique &&
        a.imageSrc === b.imageSrc &&
        normalizeRecoloredImageSrc(a.recoloredImageSrc) ===
            normalizeRecoloredImageSrc(b.recoloredImageSrc) &&
        a.recoloredId === b.recoloredId &&
        isVector2Equal(a.size, b.size) &&
        isTransformEqual(a.transform, b.transform));
};
